import { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, ChevronRight, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Slider } from "../components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList,
  BreadcrumbPage, BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../components/ui/sheet";
import ProductCard from "../components/ProductCard";
import { FILTER_CATEGORIES, BRANDS, BITOLAS, CORES, APLICACOES } from "../data/store";
import { getProducts } from "../services/woocommerce";

const SORT_OPTIONS = [
  { value: "relevance", label: "Mais relevantes" },
  { value: "price-asc", label: "Menor preço" },
  { value: "price-desc", label: "Maior preço" },
  { value: "best", label: "Mais vendidos" },
  { value: "new", label: "Lançamentos" },
];

const FilterSidebar = ({ filters, setFilters, reset }) => {
  const toggle = (key, value) =>
    setFilters((f) => {
      const arr = f[key];
      return { ...f, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });

  return (
    <div className="space-y-7">
      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-3">Categorias</h3>
        <ul className="space-y-2.5">
          {FILTER_CATEGORIES.map((c) => (
            <li key={c.slug} className="flex items-center gap-2.5">
              <Checkbox
                id={`cat-${c.slug}`}
                data-testid={`filter-category-${c.slug}`}
                checked={filters.categories.includes(c.slug)}
                onCheckedChange={() => toggle("categories", c.slug)}
              />
              <label htmlFor={`cat-${c.slug}`} className="text-sm text-slate-600 cursor-pointer select-none">
                {c.name}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-3">Preço</h3>
        <Slider
          data-testid="filter-price-slider"
          min={0}
          max={200}
          step={5}
          value={filters.price}
          onValueChange={(v) => setFilters((f) => ({ ...f, price: v }))}
          className="my-4"
        />
        <div className="flex items-center gap-2">
          <Input
            data-testid="filter-price-min"
            type="number"
            value={filters.price[0]}
            onChange={(e) => setFilters((f) => ({ ...f, price: [Number(e.target.value), f.price[1]] }))}
            className="h-9 text-sm"
          />
          <span className="text-slate-400">–</span>
          <Input
            data-testid="filter-price-max"
            type="number"
            value={filters.price[1]}
            onChange={(e) => setFilters((f) => ({ ...f, price: [f.price[0], Number(e.target.value)] }))}
            className="h-9 text-sm"
          />
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-3">Marcas</h3>
        <ul className="space-y-2.5">
          {BRANDS.map((b) => (
            <li key={b} className="flex items-center gap-2.5">
              <Checkbox
                id={`brand-${b}`}
                data-testid={`filter-brand-${b.toLowerCase()}`}
                checked={filters.brands.includes(b)}
                onCheckedChange={() => toggle("brands", b)}
              />
              <label htmlFor={`brand-${b}`} className="text-sm text-slate-600 cursor-pointer select-none">
                {b}
              </label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-3">Bitola</h3>
        <ul className="space-y-2.5">
          {BITOLAS.map((b) => (
            <li key={b} className="flex items-center gap-2.5">
              <Checkbox
                id={`bitola-${b}`}
                data-testid={`filter-bitola-${b}`}
                checked={filters.bitolas.includes(b)}
                onCheckedChange={() => toggle("bitolas", b)}
              />
              <label htmlFor={`bitola-${b}`} className="text-sm text-slate-600 cursor-pointer select-none">{b}</label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-3">Cor</h3>
        <ul className="space-y-2.5">
          {CORES.map((c) => (
            <li key={c} className="flex items-center gap-2.5">
              <Checkbox
                id={`cor-${c}`}
                data-testid={`filter-cor-${c.toLowerCase()}`}
                checked={filters.cores.includes(c)}
                onCheckedChange={() => toggle("cores", c)}
              />
              <label htmlFor={`cor-${c}`} className="text-sm text-slate-600 cursor-pointer select-none">{c}</label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-3">Aplicação</h3>
        <ul className="space-y-2.5">
          {APLICACOES.map((a) => (
            <li key={a} className="flex items-center gap-2.5">
              <Checkbox
                id={`aplic-${a}`}
                data-testid={`filter-aplicacao-${a}`}
                checked={filters.aplicacoes.includes(a)}
                onCheckedChange={() => toggle("aplicacoes", a)}
              />
              <label htmlFor={`aplic-${a}`} className="text-sm text-slate-600 cursor-pointer select-none">{a}</label>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-900 mb-3">Disponibilidade</h3>
        <ul className="space-y-2.5">
          <li className="flex items-center gap-2.5">
            <Checkbox
              id="avail-stock"
              data-testid="filter-in-stock"
              checked={filters.inStock}
              onCheckedChange={(v) => setFilters((f) => ({ ...f, inStock: !!v }))}
            />
            <label htmlFor="avail-stock" className="text-sm text-slate-600 cursor-pointer">Em estoque</label>
          </li>
          <li className="flex items-center gap-2.5">
            <Checkbox
              id="avail-promo"
              data-testid="filter-promo"
              checked={filters.onSale}
              onCheckedChange={(v) => setFilters((f) => ({ ...f, onSale: !!v }))}
            />
            <label htmlFor="avail-promo" className="text-sm text-slate-600 cursor-pointer">Promoção</label>
          </li>
        </ul>
      </div>

      <Button
        variant="outline"
        onClick={reset}
        data-testid="clear-filters-button"
        className="w-full border-slate-300 text-slate-700 hover:bg-slate-50"
      >
        Limpar filtros
      </Button>
    </div>
  );
};

const Shop = () => {
  const [params] = useSearchParams();
  const initialCat = params.get("cat");
  const query = params.get("q") || "";
  const ofertas = params.get("ofertas") === "1";

  const defaultFilters = {
    categories: initialCat ? [initialCat] : [],
    brands: [],
    bitolas: [],
    cores: [],
    aplicacoes: [],
    price: [0, 200],
    inStock: false,
    onSale: ofertas,
  };

  const [productsList, setProductsList] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const [sort, setSort] = useState("relevance");
  const [page, setPage] = useState(1);
  const perPage = 9;

  useEffect(() => {
    getProducts({ per_page: 100 }).then(setProductsList).catch(console.error);
  }, []);

  useEffect(() => {
    setFilters({
      categories: initialCat ? [initialCat] : [],
      brands: [],
      bitolas: [],
      cores: [],
      aplicacoes: [],
      price: [0, 200],
      inStock: false,
      onSale: ofertas,
    });
    setPage(1);
  }, [initialCat, ofertas]);

  const reset = () =>
    setFilters({ categories: [], brands: [], bitolas: [], cores: [], aplicacoes: [], price: [0, 200], inStock: false, onSale: false });

  const filtered = useMemo(() => {
    let list = productsList.filter((p) => {
      if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
      if (filters.categories.length && !filters.categories.includes(p.categorySlug)) return false;
      if (filters.brands.length && !filters.brands.includes(p.brand)) return false;
      if (filters.bitolas.length && !filters.bitolas.includes(p.bitola)) return false;
      if (filters.cores.length && !filters.cores.includes(p.cor)) return false;
      if (filters.aplicacoes.length && !filters.aplicacoes.includes(p.aplicacao)) return false;
      if (p.price < filters.price[0] || p.price > filters.price[1]) return false;
      if (filters.onSale && !(p.oldPrice && p.oldPrice > p.price)) return false;
      return true;
    });
    if (sort === "price-asc") list = [...list].sort((a, b) => a.price - b.price);
    if (sort === "price-desc") list = [...list].sort((a, b) => b.price - a.price);
    if (sort === "best") list = [...list].sort((a, b) => (b.bestSeller ? 1 : 0) - (a.bestSeller ? 1 : 0));
    if (sort === "new") list = [...list].sort((a, b) => b.id - a.id);
    return list;
  }, [filters, sort, query, productsList]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * perPage, currentPage * perPage);
  const displayCount = filtered.length > 0 ? `${filtered.length}` : "0";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild><Link to="/">Início</Link></BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage>Produtos</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6">
        {ofertas ? "Ofertas" : "Todos os produtos"}
      </h1>

      <div className="flex gap-8">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-24">
            <FilterSidebar filters={filters} setFilters={setFilters} reset={reset} />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-3 mb-5">
            <p className="text-sm text-slate-600" data-testid="results-count">
              <span className="font-semibold text-slate-900">{displayCount}</span> produtos encontrados
            </p>
            <div className="flex items-center gap-2">
              {/* Mobile filter */}
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="lg:hidden border-slate-300" data-testid="mobile-filter-button">
                    <SlidersHorizontal className="h-4 w-4 mr-2" /> Filtrar
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 overflow-y-auto">
                  <SheetHeader className="mb-4"><SheetTitle>Filtros</SheetTitle></SheetHeader>
                  <FilterSidebar filters={filters} setFilters={setFilters} reset={reset} />
                </SheetContent>
              </Sheet>

              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-44 h-10 border-slate-300" data-testid="sort-select">
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {pageItems.length === 0 ? (
            <div className="py-24 text-center border border-dashed border-slate-300 rounded-lg">
              <p className="text-slate-500">Nenhum produto encontrado com os filtros selecionados.</p>
              <Button variant="outline" className="mt-4" onClick={reset}>Limpar filtros</Button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6" data-testid="shop-grid">
              {pageItems.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-10 flex items-center justify-center gap-2" data-testid="pagination">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  data-testid={`page-${n}`}
                  className={`h-10 w-10 rounded-md text-sm font-semibold transition-colors ${
                    n === currentPage
                      ? "bg-brand text-white"
                      : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Shop;
